/**
 * Design staging (Phase 65 / BLU-594 PR3b).
 *
 * Per (product, slot) assignment for the apply engine. Three source kinds:
 *   - `upload` — an operator drop; bytes go to GCS via a presigned PUT
 *     (`mintDesignMasterUploadUrl`). Tracks its own state
 *     (staged → uploading → uploaded | error) and holds the minted `key` once
 *     uploaded. Object-URL thumb revoked on replace/remove/unmount.
 *   - `drive` — a confirmed Google Drive candidate (`fileId`); materialized in
 *     the approved run.
 *   - `url` — an operator-pasted, server-validated URL (`previewDesignUrl`).
 *
 * Only an `uploaded` upload (or any drive/url) is "ready" — i.e. yields a
 * `SourceRef` the sync scope can reference. Kept at page level so it survives a
 * row collapse; discarded (URLs revoked) on unmount.
 *
 * Phase 86 (D-01, D-21): a product whose type has Platen Size rules stages
 * one slot set PER PLATEN VARIANT, keyed `<platen>:<variant>|<set>-<side>`
 * (`platenSlotKey`), so a Small and a Large file for the same set and side
 * never share a slot. Beside the slots, each ruled product keeps a
 * `PlatenLayout` snapshot that `DesignsTier` writes whenever its groups
 * change: the page builds the run scope and the missing-files list from it,
 * because a selection can span pages and the page only holds variants for the
 * products it is showing.
 *
 * Phase 86 slice B (86-28, R3-8, R3-12): for an org that uses its own design
 * IDs, each product keeps a `ByoDesignChoice` the same way: the design ID and
 * each variant's light, dark or medium from its metafields (written by the
 * row's BYO panel), plus the operator's choices. The page builds the run's
 * design code and `variantSets` from it and never mints a code. A medium set
 * stages under `medium-{side}` slots like any other set.
 *
 * Round 4 (R4-12): Platen variants settings are DEFAULTS. Per (product,
 * platen) the operator may choose "Upload Small by hand instead": the page
 * then never makes that product's Small from Large on that platen (By colour
 * here, By set slots in 86-32). Browser state only, no table and no read: it
 * matters only until the own Small is published, and from then on the
 * server's keep rule (the made marker, R4-4) keeps that file in any session.
 * By colour can also choose it for ONE colour (`setSmallByHandColour`): that
 * colour's Small row shows and nothing is made for it; the platen-wide choice
 * (By set's) still counts for every colour.
 *
 * Back from the Upload Studio: given a page session scope (`lib/page-session`),
 * every value lives in the page session store instead of component state, so
 * an upload that finishes while the page is away still lands, and the page's
 * next visit reads it all back. The page owns the visit: when it is left
 * without keeping its session, it calls `discardStagedDesigns` (object URLs
 * revoked) before the values are dropped. Without a scope (no org yet, a
 * test) it is plain component state, revoked on unmount as before.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DerivedDesignSet, DesignSet, DtgPlatenCode, PlatenVariant } from '@threadheads/shared';
import { readPageSession, usePageSessionState, type PageSessionScope } from '@/lib/page-session';

/** The print set type lives in `@threadheads/shared` beside `requiredSets` (Phase 86). */
export type { DesignSet };
export type DesignSide = 'front' | 'back';
/** A plain slot: products without platen rules (today's shape). */
export type PlainSlotKey = `${DesignSet}-${DesignSide}`;
/** A platen variant slot: `<platen>:<variant>|<set>-<side>`, e.g. `14x16:Small|dark-front`. */
export type PlatenSlotKey = `${string}|${DesignSet}-${DesignSide}`;
export type SlotKey = PlainSlotKey | PlatenSlotKey;

export type UploadState = 'staged' | 'uploading' | 'uploaded' | 'error';

export interface UploadAssignment {
  kind: 'upload';
  file: File;
  /** Object URL for the thumbnail — revoked when replaced/removed/unmounted. */
  url: string;
  state: UploadState;
  /** GCS key returned by `mintDesignMasterUploadUrl`, present once `uploaded`. */
  key?: string;
  error?: string;
}

export interface DriveAssignment {
  kind: 'drive';
  fileId: string;
  name: string;
  size?: number;
}

export interface UrlAssignment {
  kind: 'url';
  url: string;
  contentType?: string;
  byteSize?: number;
}

export type SlotAssignment = UploadAssignment | DriveAssignment | UrlAssignment;
export type ProductStaging = Partial<Record<SlotKey, SlotAssignment>>;

export function slotKey(set: DesignSet, side: DesignSide): PlainSlotKey {
  return `${set}-${side}`;
}

/** The slot key for one platen variant group (`platenVariantKey` from the shared module). */
export function platenSlotKey(platenVariantKey: string, set: DesignSet, side: DesignSide): PlatenSlotKey {
  return `${platenVariantKey}|${set}-${side}`;
}

/** One platen variant group of one product, as the run scope and the missing-files list need it. */
export interface PlatenLayoutGroup {
  /** `<platen>:<variant>`, the `platenSets` key. */
  key: string;
  platen: DtgPlatenCode;
  platenVariant: PlatenVariant;
  /** Size codes, smallest first (for the "{range}" copy). */
  sizes: string[];
  /** Synced SKUs per print set, through the shared `resolveDesignSet`. */
  skusBySet: Partial<Record<DesignSet, string[]>>;
}

/** Snapshot of a ruled product's platen variant groups (86-12's grouping, not a second one). */
export interface PlatenLayout {
  productTypeName: string;
  groups: PlatenLayoutGroup[];
  /**
   * Round 4 (86-32): the layout's platens whose rules org (the 3PL's for a
   * supply product) makes Small from Large. Absent when none, so every
   * layout without a preset compares equal to before.
   */
  smallPresetPlatens?: readonly DtgPlatenCode[];
}

/**
 * Phase 86 slice B: one BYO product's design identity and the operator's
 * light, dark or medium choices. Absent for every product of every other org.
 */
export interface ByoDesignChoice {
  /** The product's own design ID (the reader's; never minted). */
  designCode: string;
  /** Each variant's letter as its metafields read it (R3-12 pre-selection). */
  metafieldSets: Record<string, DerivedDesignSet>;
  /** The operator's choices per variant id; sent as the scope's `variantSets`. */
  chosen: Record<string, DerivedDesignSet>;
  /** "Keep as is" on the front and back warning (R3-9). */
  warningKept: boolean;
}

/** Each variant's letter: the operator's choice, else its metafield's. */
export function effectiveByoSets(choice: ByoDesignChoice): Record<string, DerivedDesignSet> {
  return { ...choice.metafieldSets, ...choice.chosen };
}

const EMPTY: ProductStaging = {};
/** The stable answer for a product with no Small chosen by hand. */
const NO_PLATENS: readonly DtgPlatenCode[] = Object.freeze([]);
/** The stable answer for a product with no colour chosen by hand. */
const NO_COLOURS: readonly string[] = Object.freeze([]);

/** One colour row on one platen: `<platen>|<colour row key>` (the `smallByHandColoursFor` entries). */
export function smallByHandColourKey(platen: DtgPlatenCode, rowKey: string): string {
  return `${platen}|${rowKey}`;
}
const colourChoiceKey = smallByHandColourKey;

/** The page session keys (one per kind of value). */
const SESSION_KEYS = {
  slots: 'staged.slots',
  layouts: 'staged.layouts',
  byo: 'staged.byo',
  smallByHand: 'staged.smallByHand',
  smallByHandColour: 'staged.smallByHandColour',
} as const;

function revokeAll(byProduct: Record<string, ProductStaging> | undefined): void {
  for (const staging of Object.values(byProduct ?? {})) {
    for (const a of Object.values(staging)) {
      if (a?.kind === 'upload') URL.revokeObjectURL(a.url);
    }
  }
}

/** Let go of the staged uploads kept in the page session (before the page drops it). */
export function discardStagedDesigns(scope: PageSessionScope | null): void {
  revokeAll(readPageSession<Record<string, ProductStaging>>(scope, SESSION_KEYS.slots));
}

export interface StagedDesignsApi {
  get: (productId: string) => ProductStaging;
  /** Replace (or clear with `null`) a slot's assignment; revokes a superseded upload URL. */
  setAssignment: (productId: string, slot: SlotKey, assignment: SlotAssignment | null) => void;
  /** Patch an existing upload assignment in place (state / key / error transitions). */
  patchUpload: (productId: string, slot: SlotKey, patch: Partial<Omit<UploadAssignment, 'kind'>>) => void;
  /** The product's platen variant layout, or `null` when it has no rules (or was never opened). */
  getPlatenLayout: (productId: string) => PlatenLayout | null;
  /** Written by `DesignsTier` whenever its groups change; `null` for a product without rules. */
  setPlatenLayout: (productId: string, layout: PlatenLayout | null) => void;
  /** Phase 86 slice B: the product's BYO design choice, or `null` (not BYO, or never opened). */
  getByo: (productId: string) => ByoDesignChoice | null;
  /**
   * Written by the BYO panel from the preview row. An equal base is a no-op;
   * the same design ID keeps the operator's choices (for variants still
   * present), a changed one starts over. `null` drops it.
   */
  setByoBase: (productId: string, base: { designCode: string; metafieldSets: Record<string, DerivedDesignSet> } | null) => void;
  /** The operator picks a set for some variants (one colour, or "Make all ..."). */
  chooseByoSets: (productId: string, choices: Record<string, DerivedDesignSet>) => void;
  /** "Keep as is": dismiss the front and back warning for the product. */
  keepByoWarning: (productId: string) => void;
  /** "Undo changes": drop the operator's choices and the kept warning (back to the store's values). */
  resetByo: (productId: string) => void;
  /** R4-12: this product uploads its Small by hand on this platen (default false: made from Large). */
  isSmallByHand: (productId: string, platen: DtgPlatenCode) => boolean;
  /** R4-12: choose (or drop) Small by hand for one product and platen. Unchanged is a no-op. */
  setSmallByHand: (productId: string, platen: DtgPlatenCode, byHand: boolean) => void;
  /** R4-12: the platens this product uploads its Small by hand on; a stable empty list for none. */
  smallByHandFor: (productId: string) => readonly DtgPlatenCode[];
  /**
   * 86-34 review: the choice as of the last `setSmallByHand`, even before the
   * page re-renders (stable). The page's derive queue reads it when a job
   * starts, so a switch made in the same click is already seen. With a colour
   * row key, a choice for that one colour counts too.
   */
  isSmallByHandNow: (productId: string, platen: DtgPlatenCode, rowKey?: string) => boolean;
  /**
   * By colour: this colour's Small on this platen is uploaded by hand, by its
   * own choice or the platen-wide one.
   */
  isSmallByHandColour: (productId: string, platen: DtgPlatenCode, rowKey: string) => boolean;
  /**
   * By colour: choose (or drop) Small by hand for ONE colour. Dropping it
   * while the platen-wide choice is on turns that off and keeps every other
   * colour in `colourRowKeys` by hand, one by one. Unchanged is a no-op.
   */
  setSmallByHandColour: (
    productId: string,
    platen: DtgPlatenCode,
    rowKey: string,
    byHand: boolean,
    colourRowKeys?: readonly string[],
  ) => void;
  /** The product's colour choices (`<platen>|<row key>`); a stable empty list for none. */
  smallByHandColoursFor: (productId: string) => readonly string[];
  /**
   * The run scope's Small by hand platens: the platen-wide choice plus every
   * platen one colour chose by hand (a sync run cannot tell colours apart, so
   * one colour by hand stops the whole platen's Small being made, never makes
   * one). Platen order of first choice, once each.
   */
  smallByHandPlatensFor: (productId: string) => readonly DtgPlatenCode[];
}

function sameLayout(a: PlatenLayout | null | undefined, b: PlatenLayout | null | undefined): boolean {
  if (!a || !b) return (a ?? null) === (b ?? null);
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useStagedDesigns(scope: PageSessionScope | null = null): StagedDesignsApi {
  const [byProduct, setByProduct, getByProduct] = usePageSessionState<Record<string, ProductStaging>>(
    scope,
    SESSION_KEYS.slots,
    {},
  );
  const [layouts, setLayouts] = usePageSessionState<Record<string, PlatenLayout>>(scope, SESSION_KEYS.layouts, {});
  const [byoChoices, setByoChoices] = usePageSessionState<Record<string, ByoDesignChoice>>(scope, SESSION_KEYS.byo, {});
  const [smallByHand, setSmallByHandState, getSmallByHand] = usePageSessionState<
    Record<string, readonly DtgPlatenCode[]>
  >(scope, SESSION_KEYS.smallByHand, {});
  const [smallByHandColour, setSmallByHandColourState, getSmallByHandColour] = usePageSessionState<
    Record<string, readonly string[]>
  >(scope, SESSION_KEYS.smallByHandColour, {});

  // Without a scope the staging dies with the component: revoke every live
  // URL on unmount. With one, the page's visit decides (`discardStagedDesigns`).
  const unscopedRef = useRef(scope === null);
  unscopedRef.current = scope === null;
  const getByProductRef = useRef(getByProduct);
  getByProductRef.current = getByProduct;
  useEffect(() => {
    return () => {
      if (unscopedRef.current) revokeAll(getByProductRef.current());
    };
  }, []);

  const get = useCallback((productId: string): ProductStaging => byProduct[productId] ?? EMPTY, [byProduct]);

  const setAssignment = useCallback((productId: string, slot: SlotKey, assignment: SlotAssignment | null) => {
    setByProduct((prev) => {
      const current = prev[productId] ?? {};
      const previous = current[slot];
      if (previous?.kind === 'upload' && previous.url && previous !== assignment) {
        URL.revokeObjectURL(previous.url);
      }
      const next: ProductStaging = { ...current };
      if (assignment) next[slot] = assignment;
      else delete next[slot];
      return { ...prev, [productId]: next };
    });
  }, [setByProduct]);

  const patchUpload = useCallback((productId: string, slot: SlotKey, patch: Partial<Omit<UploadAssignment, 'kind'>>) => {
    setByProduct((prev) => {
      const current = prev[productId] ?? {};
      const existing = current[slot];
      if (existing?.kind !== 'upload') return prev;
      return { ...prev, [productId]: { ...current, [slot]: { ...existing, ...patch } } };
    });
  }, [setByProduct]);

  const getPlatenLayout = useCallback((productId: string): PlatenLayout | null => layouts[productId] ?? null, [layouts]);

  // An equal snapshot keeps the previous state object, so the writer's effect
  // (which runs on every regrouping) can never loop the page.
  const setPlatenLayout = useCallback((productId: string, layout: PlatenLayout | null) => {
    setLayouts((prev) => {
      if (sameLayout(prev[productId], layout)) return prev;
      const next = { ...prev };
      if (layout) next[productId] = layout;
      else delete next[productId];
      return next;
    });
  }, [setLayouts]);

  const getByo = useCallback((productId: string): ByoDesignChoice | null => byoChoices[productId] ?? null, [byoChoices]);

  const setByoBase = useCallback(
    (productId: string, base: { designCode: string; metafieldSets: Record<string, DerivedDesignSet> } | null) => {
      setByoChoices((prev) => {
        const current = prev[productId];
        if (!base) {
          if (!current) return prev;
          const next = { ...prev };
          delete next[productId];
          return next;
        }
        if (
          current &&
          current.designCode === base.designCode &&
          JSON.stringify(current.metafieldSets) === JSON.stringify(base.metafieldSets)
        ) {
          return prev;
        }
        const sameDesign = current?.designCode === base.designCode;
        const chosen: Record<string, DerivedDesignSet> = {};
        if (sameDesign) {
          for (const [variantId, set] of Object.entries(current.chosen)) {
            if (variantId in base.metafieldSets) chosen[variantId] = set;
          }
        }
        return {
          ...prev,
          [productId]: { ...base, chosen, warningKept: sameDesign ? current.warningKept : false },
        };
      });
    },
    [setByoChoices],
  );

  const chooseByoSets = useCallback((productId: string, choices: Record<string, DerivedDesignSet>) => {
    setByoChoices((prev) => {
      const current = prev[productId];
      if (!current) return prev;
      return { ...prev, [productId]: { ...current, chosen: { ...current.chosen, ...choices } } };
    });
  }, [setByoChoices]);

  const keepByoWarning = useCallback((productId: string) => {
    setByoChoices((prev) => {
      const current = prev[productId];
      if (!current || current.warningKept) return prev;
      return { ...prev, [productId]: { ...current, warningKept: true } };
    });
  }, [setByoChoices]);

  const resetByo = useCallback((productId: string) => {
    setByoChoices((prev) => {
      const current = prev[productId];
      if (!current || (Object.keys(current.chosen).length === 0 && !current.warningKept)) return prev;
      return { ...prev, [productId]: { ...current, chosen: {}, warningKept: false } };
    });
  }, [setByoChoices]);

  const smallByHandFor = useCallback(
    (productId: string): readonly DtgPlatenCode[] => smallByHand[productId] ?? NO_PLATENS,
    [smallByHand],
  );

  const isSmallByHand = useCallback(
    (productId: string, platen: DtgPlatenCode): boolean => (smallByHand[productId] ?? NO_PLATENS).includes(platen),
    [smallByHand],
  );

  // Unchanged keeps the previous state object, so a repeated choice never
  // re-renders the page. The store's latest value (`getSmallByHand`) is the
  // choice as of the last call, read at once by `isSmallByHandNow`.
  const setSmallByHand = useCallback(
    (productId: string, platen: DtgPlatenCode, byHand: boolean) => {
      setSmallByHandState((prev) => {
        const current = prev[productId] ?? NO_PLATENS;
        if (current.includes(platen) === byHand) return prev;
        const nextList = byHand
          ? Object.freeze([...current, platen])
          : Object.freeze(current.filter((p) => p !== platen));
        const next = { ...prev };
        if (nextList.length > 0) next[productId] = nextList;
        else delete next[productId];
        return next;
      });
    },
    [setSmallByHandState],
  );

  const smallByHandColoursFor = useCallback(
    (productId: string): readonly string[] => smallByHandColour[productId] ?? NO_COLOURS,
    [smallByHandColour],
  );

  const smallByHandPlatensFor = useCallback(
    (productId: string): readonly DtgPlatenCode[] => {
      const platenWide = smallByHand[productId] ?? NO_PLATENS;
      const colours = smallByHandColour[productId] ?? NO_COLOURS;
      if (colours.length === 0) return platenWide;
      const out = new Set<DtgPlatenCode>(platenWide);
      for (const key of colours) out.add(key.slice(0, key.indexOf('|')) as DtgPlatenCode);
      return [...out];
    },
    [smallByHand, smallByHandColour],
  );

  const isSmallByHandColour = useCallback(
    (productId: string, platen: DtgPlatenCode, rowKey: string): boolean =>
      (smallByHand[productId] ?? NO_PLATENS).includes(platen) ||
      (smallByHandColour[productId] ?? NO_COLOURS).includes(colourChoiceKey(platen, rowKey)),
    [smallByHand, smallByHandColour],
  );

  const setColourChoices = useCallback(
    (productId: string, changes: ReadonlyArray<[string, boolean]>) => {
      setSmallByHandColourState((prev) => {
        const current = prev[productId] ?? NO_COLOURS;
        let list = current;
        for (const [key, on] of changes) {
          if (list.includes(key) === on) continue;
          list = on ? [...list, key] : list.filter((k) => k !== key);
        }
        if (list === current) return prev;
        const next = { ...prev };
        if (list.length > 0) next[productId] = Object.freeze([...list]);
        else delete next[productId];
        return next;
      });
    },
    [setSmallByHandColourState],
  );

  const setSmallByHandColour = useCallback(
    (productId: string, platen: DtgPlatenCode, rowKey: string, byHand: boolean, colourRowKeys: readonly string[] = []) => {
      const platenWide = (getSmallByHand()[productId] ?? NO_PLATENS).includes(platen);
      if (byHand) {
        if (!platenWide) setColourChoices(productId, [[colourChoiceKey(platen, rowKey), true]]);
        return;
      }
      if (platenWide) {
        // The platen-wide choice covers every colour: drop it, and keep each
        // other colour by hand on its own.
        setSmallByHand(productId, platen, false);
        setColourChoices(productId, [
          ...colourRowKeys.filter((k) => k !== rowKey).map((k): [string, boolean] => [colourChoiceKey(platen, k), true]),
          [colourChoiceKey(platen, rowKey), false],
        ]);
        return;
      }
      setColourChoices(productId, [[colourChoiceKey(platen, rowKey), false]]);
    },
    [getSmallByHand, setColourChoices, setSmallByHand],
  );

  const isSmallByHandNow = useCallback(
    (productId: string, platen: DtgPlatenCode, rowKey?: string): boolean =>
      (getSmallByHand()[productId] ?? NO_PLATENS).includes(platen) ||
      (rowKey !== undefined && (getSmallByHandColour()[productId] ?? NO_COLOURS).includes(colourChoiceKey(platen, rowKey))),
    [getSmallByHand, getSmallByHandColour],
  );

  return useMemo(
    () => ({
      get,
      setAssignment,
      patchUpload,
      getPlatenLayout,
      setPlatenLayout,
      getByo,
      setByoBase,
      chooseByoSets,
      keepByoWarning,
      resetByo,
      isSmallByHand,
      setSmallByHand,
      smallByHandFor,
      smallByHandPlatensFor,
      isSmallByHandNow,
      isSmallByHandColour,
      setSmallByHandColour,
      smallByHandColoursFor,
    }),
    [
      get,
      setAssignment,
      patchUpload,
      getPlatenLayout,
      setPlatenLayout,
      getByo,
      setByoBase,
      chooseByoSets,
      keepByoWarning,
      resetByo,
      isSmallByHand,
      setSmallByHand,
      smallByHandFor,
      smallByHandPlatensFor,
      isSmallByHandNow,
      isSmallByHandColour,
      setSmallByHandColour,
      smallByHandColoursFor,
    ],
  );
}
